"use client";

import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import * as React from "react";

import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import useTranslation from "@/hooks/use-translation";
import { generateUniqueKey } from "@/lib/utils";

import { DataTablePagination } from "./data-table-pagination";

/**
 * Universal DataTable component
 * - Mendukung client-side & server-side pagination/filtering
 * - Skeleton loading baris dummy
 * - Search universal
 * - API props fleksibel
 * - Row selection support
 */
export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pageCount?: number; // Untuk server-side pagination
  searchKey?: string;
  placeholder?: string;
  onRowClick?: (row: TData, event?: React.MouseEvent) => void;
  showSearch?: boolean;
  "data-cy"?: string;
  pagination?: PaginationState; // Untuk server-side
  setPagination?: (
    updater: PaginationState | ((prevState: PaginationState) => PaginationState)
  ) => void;
  isLoading?: boolean;
  manualFiltering?: boolean;
  onSearchChange?: (value: string) => void;
  getRowClassName?: (row: TData) => string;
  skeletonRowCount?: number;
  noResultsMessage?: string; // Optional specific no results message
  searchTerm?: string; // Optional search term for dynamic messages
  searchActions?: React.ReactNode; // Custom actions to show next to search box
  enableRowSelection?: boolean; // Enable row selection
  selectedRows?: string[]; // Array of selected row IDs
  onSelectionChange?: (selectedIds: string[]) => void; // Callback when selection changes
  getRowId?: (row: TData) => string; // Function to get unique row ID
}

export function DataTable<TData, TValue>({
  columns,
  data,
  pageCount,
  searchKey,
  placeholder,
  onRowClick,
  showSearch = true,
  "data-cy": dataCy,
  pagination,
  setPagination,
  isLoading,
  manualFiltering = false,
  onSearchChange,
  getRowClassName,
  skeletonRowCount = 5,
  noResultsMessage,
  searchTerm,
  searchActions,
  enableRowSelection = false,
  selectedRows = [],
  onSelectionChange,
  getRowId,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const prevSelectedRowsRef = React.useRef<string[]>([]);
  const onSelectionChangeRef = React.useRef(onSelectionChange);

  // Initialize rowSelection from selectedRows
  React.useEffect(() => {
    if (enableRowSelection && getRowId) {
      const selectionState: RowSelectionState = {};
      if (selectedRows && selectedRows.length > 0) {
        selectedRows.forEach((id) => {
          selectionState[id] = true;
        });
      }
      setRowSelection(selectionState);
      prevSelectedRowsRef.current = selectedRows || [];
    }
  }, [selectedRows, enableRowSelection, getRowId]);

  // Update ref when onSelectionChange changes
  React.useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  // Handle row selection change from table
  const handleRowSelectionChange = React.useCallback(
    (updater: any) => {
      const newSelection =
        typeof updater === "function" ? updater(rowSelection) : updater;
      setRowSelection(newSelection);

      // Notify parent with only the selected IDs
      if (enableRowSelection && onSelectionChangeRef.current && getRowId) {
        const selectedIds = Object.keys(newSelection).filter(
          (key) => newSelection[key]
        );
        // Only call onSelectionChange if the selection actually changed
        const prevSelectedIds = prevSelectedRowsRef.current;
        if (
          JSON.stringify(selectedIds.sort()) !==
          JSON.stringify(prevSelectedIds.sort())
        ) {
          prevSelectedRowsRef.current = selectedIds;
          onSelectionChangeRef.current(selectedIds);
        }
      }
    },
    [enableRowSelection, getRowId, rowSelection]
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: pageCount,
    state: {
      ...(pagination ? { pagination } : {}),
      columnFilters,
      ...(enableRowSelection ? { rowSelection } : {}),
    },
    manualPagination: !!pagination,
    manualFiltering: manualFiltering,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    ...(enableRowSelection
      ? { onRowSelectionChange: handleRowSelectionChange }
      : {}),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(getRowId ? { getRowId } : {}),
  });

  // Handle search input change
  const handleSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchValue(value);

      if (manualFiltering && onSearchChange) {
        onSearchChange(value);
      } else if (searchKey) {
        table.getColumn(searchKey)?.setFilterValue(value);
      }
    },
    [manualFiltering, onSearchChange, searchKey, table]
  );

  // Get the appropriate no results message
  const getNoResultsMessage = () => {
    // If specific message is provided, use it
    if (noResultsMessage) {
      return noResultsMessage;
    }

    // If search term is provided and we have a search context, try to get specific message
    if (searchTerm && searchTerm.trim()) {
      // Try to get module-specific message based on searchKey or context
      if (searchKey === "shop_name" || searchKey === "name") {
        return t("shops.noShopsFound", { searchTerm }) || t("common.noResults");
      }
      if (searchKey === "customer" || searchKey === "customer_name") {
        return t("bookings.noBookings") || t("common.noResults");
      }
    }

    // Fallback to common no results message
    return t("common.noResults") || "No results.";
  };

  // Skeleton baris dummy
  const renderSkeletonRows = () => {
    return Array.from({ length: skeletonRowCount }).map((_, idx) => (
      <TableRow key={generateUniqueKey(`skeleton-row-${idx}`)}>
        {columns.map((col, cidx) => (
          <TableCell key={generateUniqueKey(`skeleton-cell-${cidx}`)}>
            <div
              className="bg-muted/40 animate-pulse rounded"
              style={{
                height: 20,
                width: `${60 + Math.random() * 30}%`, // lebar acak 60-90%
                margin: "6px 0",
              }}
            />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {showSearch && searchKey && (
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex flex-1 items-center space-x-2">
            <Input
              placeholder={placeholder || t("common.search") || "Search..."}
              value={searchValue}
              onChange={handleSearchChange}
              className="max-w-sm text-sm"
              data-cy="data-table-search"
            />
          </div>
          {searchActions && (
            <div className="flex items-center space-x-2">{searchActions}</div>
          )}
        </div>
      )}
      <div className="overflow-x-auto rounded-md border">
        <Table data-cy={dataCy} className="w-full" wrapperClassName="">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={generateUniqueKey(headerGroup.id)}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={generateUniqueKey(header.id)}
                      className="text-xs whitespace-nowrap sm:text-sm"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody aria-busy={isLoading ? "true" : undefined}>
            {isLoading ? (
              renderSkeletonRows()
            ) : data.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={generateUniqueKey(row.id)}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={(event) => onRowClick?.(row.original, event)}
                  className={` ${onRowClick ? "hover:bg-muted/50 cursor-pointer" : ""} ${getRowClassName ? getRowClassName(row.original) : ""} `}
                  data-cy="table-row"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={generateUniqueKey(cell.id)}
                      className="text-xs whitespace-nowrap sm:text-sm"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {getNoResultsMessage()}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
